"use client";

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import QRCode from "qrcode.react";
import { toPng } from "html-to-image";
import { Download } from "lucide-react";
import { useParams } from "react-router-dom";

// Keep as fallback only
const defaultElementStyles = {
  "profilePicture": {
    "bottom": 160,
    "left": "50%",
    "size": 170
  },
  "name": {
    "top": 163,
    "left": "50%",
    "fontSize": 29,
    "color": "black"
  },
  "institute": {
    "bottom": 293,
    "left": "50%",
    "fontSize": 18,
    "color": "black"
  },
  "designation": {
    "bottom": 304,
    "left": "50%",
    "fontSize": 16,
    "color": "black"
  },
  "qrCode": {
    "bottom": 215,
    "left": "50%",
    "size": 100
  },
  "participantId": {
    "bottom": 3,
    "left": "50%",
    "fontSize": 12,
    "color": "black"
  },
  "ticketInfo": {
    "bottom": 174,
    "left": "50%",
    "fontSize": 16,
    "color": "black"
  },
  "ticketDates": {
    "bottom": 102,
    "left": "50%",
    "fontSize": 17,
    "color": "black"
  }
};

const PLACEHOLDER_PROFILE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Ccircle cx='100' cy='100' r='100' fill='%23e2e8f0'/%3E%3Ccircle cx='100' cy='85' r='40' fill='%23a0aec0'/%3E%3Cpath d='M160 155c0-33.1-26.9-60-60-60s-60 26.9-60 60' fill='%23a0aec0'/%3E%3C/svg%3E";

export default function LinkIDCard() {
  const params = useParams();
  const eventId = params?.eventId;
  const participantId = params?.participantId;

  // State for design settings from database
  const [elementStyles, setElementStyles] = useState(defaultElementStyles);
  const [globalVisibility, setGlobalVisibility] = useState({
    name: true,
    profilePicture: true,
    institute: true,
    designation: true,
    qrCode: true,
    participantId: true,
    ticketDates: false,
    ticketInfo: false
  });

  const [participant, setParticipant] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [qrCodeSize, setQrCodeSize] = useState(100);
  const [error, setError] = useState("");
  const idCardRef = useRef(null);

  const [eventData, setEventData] = useState(null);

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/events/${eventId}`);
        setEventData(response.data);
      } catch (error) {
        console.error('Error fetching event data:', error);
      }
    };

    if (eventId) {
      fetchEventData();
    }
  }, [eventId]);

  // Resize observer for dynamic QR code sizing
  useEffect(() => {
    if (!idCardRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setQrCodeSize(Math.max(60, Math.min(width * 0.15, elementStyles.qrCode?.size || 100)));
    });

    observer.observe(idCardRef.current);
    return () => observer.disconnect();
  }, [elementStyles.qrCode?.size]);

  // Fetch design settings from database
  const fetchCardDesign = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/participants/design/${eventId}`);
      console.log("res", res.data);
      if (res.data && res.data.elementStyles) {
        setElementStyles(res.data.elementStyles);
      }
      if (res.data && res.data.visibility) {
        setGlobalVisibility(res.data.visibility);
      }
    } catch (error) {
      console.error('Error fetching card design:', error);
      // Keep using default styles on error
    }
  };

  // Fetch participant data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both participant and design data
        await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/participants/get-participant/${eventId}/${participantId}`)
            .then((res) => {
              setParticipant(res.data);
              setBackgroundImage(res.data.backgroundImage || res.data.background || "");
            }),
          fetchCardDesign()
        ]);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load participant data");
      }
    };

    if (eventId && participantId) {
      fetchData();
    }
  }, [eventId, participantId]);

  // Helper function to get ticket display info (similar to main IdCard component)
  const getTicketDisplayInfo = () => {
    const ticketDetails = participant?.ticketDetails;
    console.log("ticketDetails", ticketDetails);
    if (!ticketDetails) return null;
    const region = participant?.ticketRegion;
    const category = participant?.ticketCategory;
    const selectedDates = ticketDetails.selectedDates;
    const currency = ticketDetails.currency;
    const price = ticketDetails.discountedPrice || ticketDetails.originalPrice;

    return {
      region: region ? (region === 'indian' ? 'Indian' : 'International') : null,
      category,
      dates: selectedDates,
      currency,
      price,
      ticketType: ticketDetails.ticketType
    };
  };

  // Helper function to format ticket dates
  const formatTicketDates = (dates) => {
    if (!dates || dates.length === 0) return '';
    return dates.map(date =>
      new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short'
      })
    ).join(', ');
  };

  const downloadImage = async () => {
    const element = idCardRef.current;
    if (!element) return;

    setIsDownloading(true);
    setError("");

    try {
      const imgs = element.querySelectorAll("img");
      await Promise.all(
        Array.from(imgs).map(
          (img) =>
            new Promise((resolve, reject) => {
              if (img.complete && img.naturalHeight !== 0) resolve();
              else {
                img.onload = resolve;
                img.onerror = reject;
              }
            })
        )
      );

      const dataUrl = await toPng(element, {
        cacheBust: true,
        backgroundColor: null,
        quality: 1,
        pixelRatio: 3,
        filter: (node) => {
          if (
            node.tagName === "LINK" &&
            node.getAttribute("rel") === "stylesheet" &&
            node.getAttribute("href")?.includes("fonts.googleapis.com")
          ) {
            return false;
          }
          return true;
        },
        skipFonts: true,
      });

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `id-card-${participantId || "download"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error("Could not download the image", err);
      setError("Failed to generate image. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!participant) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="p-4 text-center">Loading...</div>
      </div>
    );
  }

  const participantUrl = `http://idcard.insideoutprojects.in/checkin/${participant._id || participant.id}`;
  const ticketInfo = getTicketDisplayInfo();

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 font-sans scale-75">
      {/* ID Card Container */}
      <div
        ref={idCardRef}
        className="relative h-[610px] w-[430px] aspect-[430/610] bg-white rounded-sm overflow-hidden shadow-lg"
      >
        <div className="relative z-10 h-full text-white">
          {/* Background Image */}
          <div className="absolute inset-0">
            {backgroundImage && (
              <img
                src={backgroundImage || "/placeholder.svg?height=610&width=430"}
                alt="Background"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
          </div>

          <div className="relative h-full flex flex-col justify-between p-4 items-center">
            {/* Profile Picture */}
            {globalVisibility.profilePicture && participant.profilePicture && (
              <div
                style={{
                  bottom: elementStyles.profilePicture.bottom ?? undefined,
                  top: elementStyles.profilePicture.top ?? undefined,
                  left: elementStyles.profilePicture.left ?? undefined,
                  right: elementStyles.profilePicture.right ?? undefined,
                }}
                className="absolute bottom-[160px] left-[50%] transform -translate-x-1/2"
              >
                <img
                  style={{
                    objectFit: "cover",
                    width: `${elementStyles.profilePicture.size}px`,
                    height: `${elementStyles.profilePicture.size}px`,
                  }}
                  src={participant.profilePicture || PLACEHOLDER_PROFILE}
                  alt="Profile"
                  className="w-[170px] h-[170px] rounded-[2px]"
                  onError={(e) => {
                    e.target.src = PLACEHOLDER_PROFILE;
                  }}
                />
              </div>
            )}

            {/* Name */}
            {globalVisibility.name && participant.firstName && (
              <p
                style={{
                  top: elementStyles.name.top ?? undefined,
                  bottom: elementStyles.name.bottom ?? undefined,
                  left: elementStyles.name.left ?? undefined,
                  right: elementStyles.name.right ?? undefined,
                  fontSize: `${elementStyles.name.fontSize}px`,
                  color: elementStyles.name.color,
                  textAlign: elementStyles.name.textAlign
                }}
                className="absolute  w-full leading-normal whitespace-nowrap overflow-hidden text-lg transform -translate-x-1/2 font-bold  text-white mt-1 px-10 truncate"
              >
                {participant.firstName} {participant.lastName}
              </p>
            )}

            {/* Institute */}
            {globalVisibility.institute && participant.institute && (
              <p
                style={{
                  top: elementStyles.institute.top ?? undefined,
                  bottom: elementStyles.institute.bottom ?? undefined,
                  left: elementStyles.institute.left ?? undefined,
                  right: elementStyles.institute.right ?? undefined,
                  fontSize: `${elementStyles.institute.fontSize}px`,
                  color: elementStyles.institute.color,
                  textAlign: elementStyles.institute.textAlign
                }}
                className="absolute bottom-[130px] w-[420px] whitespace-nowrap overflow-hidden text-lg transform -translate-x-1/2 font-semibold text-center text-white mt-1 px-10 truncate"
                dangerouslySetInnerHTML={{
                  __html: participant.institute.toUpperCase(),
                }}
              />
            )}

            {/* Designation */}
            {globalVisibility.designation && participant.designation && (
              <p
                style={{
                  top: elementStyles.designation.top ?? undefined,
                  bottom: elementStyles.designation.bottom ?? undefined,
                  left: elementStyles.designation.left ?? undefined,
                  right: elementStyles.designation.right ?? undefined,
                  fontSize: `${elementStyles.designation.fontSize}px`,
                  color: elementStyles.designation.color,
                  textAlign: elementStyles.designation.textAlign
                }}
                className="absolute bottom-[107px] transform -translate-x-1/2 text-md font-bold text-center text-black"
              >
                {participant.designation}
              </p>
            )}

            {/* Ticket Information */}
            {globalVisibility.ticketInfo && ticketInfo && (
              <div
                style={{
                  top: elementStyles?.ticketInfo?.top ?? undefined,
                  bottom: elementStyles?.ticketInfo?.bottom ?? undefined,
                  left: elementStyles?.ticketInfo?.left ?? undefined,
                  right: elementStyles?.ticketInfo?.right ?? undefined,
                  textAlign: elementStyles?.ticketInfo?.textAlign
                }}
                className="absolute w-[420px] transform -translate-x-1/2  px-4"
              >
                <div className="space-y-2">
                  {/* Category Badge */}
                  <div
                    className=" rounded-xl px-6   inline-block font-bold tracking-wide text-black"
                    style={{
                      fontSize: `${Math.max(1, (elementStyles?.ticketInfo?.fontSize || 14) + 4)}px`,
                    }}
                  >
                    {ticketInfo?.category}

                  </div>
                </div>
              </div>
            )}

            {/* Ticket Dates */}
            {globalVisibility.ticketDates && ticketInfo && ticketInfo.dates && (
              <div
                style={{
                  top: elementStyles?.ticketDates?.top ?? undefined,
                  bottom: elementStyles?.ticketDates?.bottom ?? undefined,
                  left: elementStyles?.ticketDates?.left ?? undefined,
                  right: elementStyles?.ticketDates?.right ?? undefined,
                  textAlign: elementStyles?.ticketDates?.textAlign
                }}
                className="absolute w-[420px] transform -translate-x-1/2  px-4"
              >
                <div className="rounded-lg px-4 py-2.5 ">
                  <div
                    className="font-semibold text-black"
                    style={{
                      fontSize: `${elementStyles?.ticketDates?.fontSize}px`,
                      color: elementStyles?.ticketDates?.color,
                    }}
                  >
                    {formatTicketDates(ticketInfo.dates)}
                  </div>
                </div>
              </div>
            )}


            {/* QR Code */}
            {globalVisibility.qrCode && (
              <div
                style={{
                  top: elementStyles.qrCode.top ?? undefined,
                  bottom: elementStyles.qrCode.bottom ?? undefined,
                  left: elementStyles.qrCode.left ?? undefined,
                  right: elementStyles.qrCode.right ?? undefined,
                }}
                className="absolute rounded-xl bg-white px-2 pb-3 shadow-md"
              >
                {/* Text container limited to QR size */}
                <div
                  style={{ maxWidth: elementStyles.qrCode.size || qrCodeSize }}
                  className="mx-auto"
                >
                  <p className="text-center text-[12px] text-black font-bold py-1 break-words">
                    {eventData?.qrCodeText || "Show at the Venue"}
                  </p>
                </div>

                <QRCode
                  value={participantUrl}
                  size={elementStyles.qrCode.size || qrCodeSize}
                  level="H"
                  className="rounded-lg mx-auto"
                />
              </div>
            )}

            {/* Participant ID */}
            {globalVisibility.participantId && participant.participantId && (
              <div
                style={{
                  top: elementStyles.participantId.top ?? undefined,
                  bottom: elementStyles.participantId.bottom ?? undefined,
                  left: elementStyles.participantId.left ?? undefined,
                  right: elementStyles.participantId.right ?? undefined,
                  fontSize: `${elementStyles.participantId.fontSize}px`,
                  color: elementStyles.participantId.color,
                  textAlign: elementStyles.participantId.textAlign
                }}
                className="absolute bottom-[1px] transform -translate-x-1/2 text-xs font-bold text-center text-black"
              >
                {participant.participantId}
              </div>
            )}

            {/* Tag */}
            {participant.tag && (
              <div className="text-black font-bold bg-green-100 px-2 mt-[565px] ml-[343px]">
                {participant.tag}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-4 text-red-500 bg-red-50 p-2 rounded-md">
          {error}
        </div>
      )}

      {/* Download button */}
      <button
        onClick={downloadImage}
        disabled={isDownloading}
        className="mt-6 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-black rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isDownloading ? (
          <div className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
        ) : (
          <>
            <Download className="h-5 w-5 mr-2" />
            <span>Download your Coupon</span>
          </>
        )}
      </button>
    </div>
  );
}
